import React, { useState, useEffect, useRef } from "react";
import { db } from "../firebase/config";
import { 
  collection, 
  onSnapshot, 
  doc, 
  updateDoc, 
  addDoc, 
  query, 
  orderBy, 
  where,
  setDoc,
  deleteDoc,
  getDocs
} from "firebase/firestore";
import { 
  MessageSquare, 
  Send, 
  Plus, 
  Search, 
  Hash, 
  Clock,
  Smile
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useUsers } from "../hooks/useUsers";
import { usePOs } from "../hooks/usePOs";
import StartDMModal from "../components/modals/StartDMModal";
import CreateChannelModal from "../components/modals/CreateChannelModal";
import ManageChannelMembersModal from "../components/modals/ManageChannelMembersModal";
import ChatEmojiGiphyPicker from "../components/chat/ChatEmojiGiphyPicker";

export default function TeamsChat() {
  const { currentUser } = useAuth();
  const { users, loading: loadingUsers } = useUsers();
  const { pos } = usePOs();

  // App States
  const [activeChat, setActiveChat] = useState(null);
  const [messageInput, setMessageInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  
  // Real-time Firestore states
  const [allChats, setAllChats] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loadingChats, setLoadingChats] = useState(true);

  // DM Selection Modal State
  const [isDMModalOpen, setIsDMModalOpen] = useState(false);

  // Create Channel Modal State
  const [isCreateChannelModalOpen, setIsCreateChannelModalOpen] = useState(false);

  // Manage Channel Members Modal State
  const [isManageMembersModalOpen, setIsManageMembersModalOpen] = useState(false);

  // Right Click Context Menu State
  const [contextMenu, setContextMenu] = useState(null); // { x, y, channel }

  // Emoji & GIPHY Picker Popover State
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const messagesEndRef = useRef(null);

  const capitalizeName = (name) => {
    if (!name) return "";
    return name.split(" ").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
  };

  const getFormattedName = (u) => {
    if (!u) return "";
    return capitalizeName(u.displayName || u.name || u.email?.split("@")[0] || "User");
  };

  const currentUserFullName = capitalizeName(currentUser?.displayName || currentUser?.email?.split("@")[0] || "Me");

  // Auto-scroll helper
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const markChatAsRead = async (chatId) => {
    if (!chatId || !currentUser?.uid) return;
    try {
      const chatDocRef = doc(db, "chats", chatId);
      await updateDoc(chatDocRef, {
        [`lastRead.${currentUser.uid}`]: new Date().toISOString()
      });
    } catch (err) {
      console.error("Error marking chat as read:", err);
    }
  };

  const isChatUnreadForMe = (chat) => {
    if (!chat.lastMessageTimestamp) return false;
    if (chat.lastMessageSenderId === currentUser?.uid) return false;

    // If channel is private, check if invited
    const invited = chat.invitedMembers || [];
    if (chat.type === "channel" && invited.length > 0) {
      const isInvited = invited.includes(currentUser?.email?.toLowerCase());
      if (!isInvited) return false;
    }

    const lastReadTime = chat.lastRead?.[currentUser?.uid] || "";
    return !lastReadTime || chat.lastMessageTimestamp > lastReadTime;
  };

  useEffect(() => {
    if (activeChat) {
      markChatAsRead(activeChat.id);
    }
  }, [activeChat]);

  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages]);

  // Click outside to close context menu
  useEffect(() => {
    const handleGlobalClick = () => setContextMenu(null);
    window.addEventListener("click", handleGlobalClick);
    return () => window.removeEventListener("click", handleGlobalClick);
  }, []);

  // 1. Subscribe to all Chats in Firestore
  useEffect(() => {
    const q = collection(db, "chats");
    const unsubscribe = onSnapshot(q, async (snapshot) => {
      let chatsList = [];
      snapshot.forEach((doc) => {
        chatsList.push({ id: doc.id, ...doc.data() });
      });

      // Auto-seed default channels if there are none in the DB
      if (chatsList.length === 0 && !loadingUsers) {
        setLoadingChats(true);
        try {
          await addDoc(collection(db, "chats"), {
            name: "general",
            type: "channel",
            createdAt: new Date().toISOString(),
            lastMessage: "Welcome to the #general channel!",
            lastMessageTime: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            lastMessageTimestamp: new Date().toISOString()
          });

          await addDoc(collection(db, "chats"), {
            name: "announcements",
            type: "channel",
            createdAt: new Date().toISOString(),
            lastMessage: "Official announcements channel.",
            lastMessageTime: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            lastMessageTimestamp: new Date().toISOString()
          });
        } catch (err) {
          console.error("Error seeding default channels:", err);
        }
      }

      // Sort chats by lastMessageTimestamp descending (or createdAt)
      chatsList.sort((a, b) => {
        const timeA = a.lastMessageTimestamp || a.createdAt || "";
        const timeB = b.lastMessageTimestamp || b.createdAt || "";
        return timeB.localeCompare(timeA);
      });

      setAllChats(chatsList);
      setLoadingChats(false);
    }, (err) => {
      console.error("Error reading chats:", err);
      setLoadingChats(false);
    });

    return () => unsubscribe();
  }, [loadingUsers]);

  // 2. Subscribe to Messages of the selected activeChat
  useEffect(() => {
    if (!activeChat) {
      setMessages([]);
      return;
    }

    const messagesQuery = query(
      collection(db, "chats", activeChat.id, "messages"),
      orderBy("timestamp", "asc")
    );

    const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
      const messagesList = [];
      snapshot.forEach((doc) => {
        messagesList.push({ id: doc.id, ...doc.data() });
      });
      setMessages(messagesList);
      markChatAsRead(activeChat.id);
    }, (err) => {
      console.error("Error reading messages:", err);
    });

    return () => unsubscribe();
  }, [activeChat]);

  // 3. Handle Send Message
  const handleSendMessage = async (e) => {
    if (e) e.preventDefault();
    const text = messageInput.trim();
    if (!text || !activeChat) return;

    setMessageInput("");

    const timestamp = new Date().toISOString();
    const timeStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    const newMsg = {
      sender: currentUserFullName,
      senderEmail: currentUser?.email || "anonymous",
      senderId: currentUser?.uid || "anonymous",
      text,
      timestamp,
      time: timeStr
    };

    try {
      // Add message to Firestore subcollection
      await addDoc(collection(db, "chats", activeChat.id, "messages"), newMsg);

      // Update parent chat document
      const chatDocRef = doc(db, "chats", activeChat.id);
      await updateDoc(chatDocRef, {
        lastMessage: text,
        lastMessageTime: timeStr,
        lastMessageTimestamp: timestamp,
        lastMessageSenderId: currentUser?.uid || "anonymous",
        [`lastRead.${currentUser?.uid}`]: timestamp
      });

      // If linked to a PO, append to the PO's chatThread array
      if (activeChat.poId) {
        const poDocRef = doc(db, "pos", activeChat.poId);
        // Find existing PO to get current chatThread
        const matchedPo = pos.find(p => p.id === activeChat.poId);
        const currentThread = matchedPo?.chatThread || [];
        
        const msgToStore = {
          sender: currentUserFullName,
          text,
          time: timeStr,
          timestamp
        };

        await updateDoc(poDocRef, {
          chatThread: [...currentThread, msgToStore]
        });
      }
    } catch (err) {
      console.error("Failed to send message:", err);
    }
  };

  const handleSelectEmoji = (emojiChar) => {
    setMessageInput((prev) => prev + emojiChar);
  };

  const handleSelectGif = async (gif) => {
    if (!activeChat || !gif?.url) return;
    setIsPickerOpen(false);

    const timestamp = new Date().toISOString();
    const timeStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    const newMsg = {
      sender: currentUserFullName,
      senderEmail: currentUser?.email || "anonymous",
      senderId: currentUser?.uid || "anonymous",
      text: "",
      gifUrl: gif.url,
      type: "gif",
      timestamp,
      time: timeStr
    };

    try {
      await addDoc(collection(db, "chats", activeChat.id, "messages"), newMsg);

      const chatDocRef = doc(db, "chats", activeChat.id);
      await updateDoc(chatDocRef, {
        lastMessage: "🎬 [GIF]",
        lastMessageTime: timeStr,
        lastMessageTimestamp: timestamp,
        lastMessageSenderId: currentUser?.uid || "anonymous",
        [`lastRead.${currentUser?.uid}`]: timestamp
      });

      if (activeChat.poId) {
        const poDocRef = doc(db, "pos", activeChat.poId);
        const matchedPo = pos.find((p) => p.id === activeChat.poId);
        const currentThread = matchedPo?.chatThread || [];
        await updateDoc(poDocRef, {
          chatThread: [...currentThread, newMsg]
        });
      }
    } catch (err) {
      console.error("Failed to send GIF message:", err);
    }
  };

  // 4. Create Channel (Triggers Create Channel Modal)
  const handleCreateChannel = () => {
    setIsCreateChannelModalOpen(true);
  };

  const handleCreateChannelFromModal = async (channelName, invitedMembers) => {
    // Check if already exists
    const existing = allChats.find(c => c.type === "channel" && c.name === channelName);
    if (existing) {
      setActiveChat(existing);
      window.alert(`Channel #${channelName} already exists! Loaded channel.`);
      return;
    }

    try {
      const docRef = await addDoc(collection(db, "chats"), {
        name: channelName,
        type: "channel",
        invitedMembers: invitedMembers || [],
        createdAt: new Date().toISOString(),
        createdByEmail: currentUser?.email || "anonymous",
        createdByName: currentUserFullName,
        createdByUid: currentUser?.uid || "anonymous",
        lastMessage: `Channel #${channelName} created.`,
        lastMessageTime: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        lastMessageTimestamp: new Date().toISOString()
      });

      // Set active
      setActiveChat({
        id: docRef.id,
        name: channelName,
        type: "channel",
        invitedMembers: invitedMembers || [],
        createdByEmail: currentUser?.email || "anonymous",
        createdByName: currentUserFullName,
        createdByUid: currentUser?.uid || "anonymous"
      });
    } catch (err) {
      console.error("Failed to create channel:", err);
    }
  };

  // 5. Open Modal to Start DM
  const handleStartDM = () => {
    setIsDMModalOpen(true);
  };

  // Callback from DM User Selection Modal
  const handleStartDMFromModal = async (selectedUser) => {
    const otherEmail = selectedUser.email.toLowerCase();
    const myEmail = currentUser.email.toLowerCase();

    // Check if DM chat already exists
    const existing = allChats.find(c => {
      return c.type === "dm" && 
        c.members && 
        c.members.includes(myEmail) && 
        c.members.includes(otherEmail);
    });

    if (existing) {
      setActiveChat(existing);
      return;
    }

    try {
      const docRef = await addDoc(collection(db, "chats"), {
        name: getFormattedName(selectedUser),
        type: "dm",
        members: [myEmail, otherEmail],
        createdAt: new Date().toISOString(),
        lastMessage: "Direct message thread started.",
        lastMessageTime: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        lastMessageTimestamp: new Date().toISOString()
      });

      setActiveChat({
        id: docRef.id,
        name: getFormattedName(selectedUser),
        type: "dm",
        members: [myEmail, otherEmail]
      });
    } catch (err) {
      console.error("Failed to create DM thread:", err);
    }
  };

  // 6. Right-click context menu handler for channels
  const handleChannelContextMenu = (e, channel) => {
    e.preventDefault();
    
    // Check permission: creator or Admin
    const creatorEmail = channel.createdByEmail;
    const currentUserDoc = users.find(u => u.email?.toLowerCase() === currentUser?.email?.toLowerCase());
    const currentUserRole = currentUserDoc?.role || "Admin";
    const isCreator = creatorEmail && creatorEmail.toLowerCase() === currentUser?.email?.toLowerCase();
    const isAdmin = currentUserRole === "Admin";

    if (!isCreator && !isAdmin) {
      // If neither, do not show custom context menu
      return;
    }

    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      channel
    });
  };

  // 7. Delete Channel and Move to Archived Chats
  const handleDeleteChannel = async (channelDoc) => {
    const creatorEmail = channelDoc.createdByEmail;
    const currentUserDoc = users.find(u => u.email?.toLowerCase() === currentUser?.email?.toLowerCase());
    const currentUserRole = currentUserDoc?.role || "Admin";
    const isCreator = creatorEmail && creatorEmail.toLowerCase() === currentUser?.email?.toLowerCase();
    const isAdmin = currentUserRole === "Admin";

    if (!isCreator && !isAdmin) {
      window.alert("You do not have permission to delete this channel.");
      return;
    }

    const confirm = window.confirm(`Are you sure you want to delete the channel #${channelDoc.name}?`);
    if (!confirm) return;

    try {
      const deletedBy = {
        email: currentUser?.email || "anonymous",
        name: currentUserFullName,
        timestamp: new Date().toISOString()
      };

      // 1. Write archived chat doc to /archived_chats
      const archivedChatRef = doc(db, "archived_chats", channelDoc.id);
      await setDoc(archivedChatRef, {
        ...channelDoc,
        deletedBy,
        archivedAt: new Date().toISOString()
      });

      // 2. Move messages to /archived_chats/{chatId}/messages subcollection
      const messagesQuery = query(collection(db, "chats", channelDoc.id, "messages"));
      const messagesSnap = await getDocs(messagesQuery);

      for (const msgDoc of messagesSnap.docs) {
        await setDoc(
          doc(db, "archived_chats", channelDoc.id, "messages", msgDoc.id),
          msgDoc.data()
        );
        await deleteDoc(doc(db, "chats", channelDoc.id, "messages", msgDoc.id));
      }

      // 3. Delete active chat from /chats
      await deleteDoc(doc(db, "chats", channelDoc.id));

      // 4. Create notifications for all attached users
      const invited = channelDoc.invitedMembers || [];
      if (invited.length > 0) {
        // Invite-only channel: notify all invited users
        for (const email of invited) {
          await addDoc(collection(db, "notifications"), {
            key: `channel_deleted_${channelDoc.id}_${email}_${Date.now()}`,
            message: `Channel #${channelDoc.name} has been deleted.`,
            type: "channel_deleted",
            targetUser: email,
            createdAt: new Date().toISOString(),
            readBy: []
          });
        }
      } else {
        // Public channel: notify all users (key global)
        await addDoc(collection(db, "notifications"), {
          key: `channel_deleted_${channelDoc.id}_global_${Date.now()}`,
          message: `Channel #${channelDoc.name} has been deleted.`,
          type: "channel_deleted",
          createdAt: new Date().toISOString(),
          readBy: []
        });
      }

      // Reset active chat if it was the deleted one
      if (activeChat && activeChat.id === channelDoc.id) {
        setActiveChat(null);
      }

      window.alert(`Successfully deleted and archived channel #${channelDoc.name}.`);
    } catch (err) {
      console.error("Failed to delete channel:", err);
      window.alert(`Error deleting channel: ${err.message}`);
    }
  };

  // --- Filtering threads list based on type & search ---
  const channels = allChats.filter(c => {
    if (c.type !== "channel") return false;
    const matchesSearch = c.name.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Check if user is invited
    const invited = c.invitedMembers || [];
    const isInvited = invited.length === 0 || invited.includes(currentUser.email.toLowerCase());
    
    return matchesSearch && isInvited;
  });
  const dms = allChats.filter(c => {
    if (c.type !== "dm") return false;
    const isMember = c.members && c.members.includes(currentUser.email.toLowerCase());
    const matchesSearch = c.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          c.members.some(m => m.toLowerCase().includes(searchQuery.toLowerCase()));
    return isMember && matchesSearch;
  });

  // Helper to resolve DM name (the other member)
  const getDMDisplayName = (chat) => {
    if (chat.type !== "dm") return chat.name;
    const otherEmail = chat.members?.find(m => m.toLowerCase() !== currentUser.email.toLowerCase());
    const matchedUser = users.find(u => u.email?.toLowerCase() === otherEmail?.toLowerCase());
    return matchedUser ? getFormattedName(matchedUser) : chat.name;
  };

  return (
    <div style={{ height: "calc(100vh - 6.5rem)", display: "flex", flexDirection: "column", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif" }}>
      
      {/* Workspace Wrapper */}
      <div className="card glass-card" style={{ 
        flex: 1, 
        display: "flex", 
        overflow: "hidden", 
        padding: 0, 
        backgroundColor: "rgba(20, 24, 33, 0.8)",
        border: "1px solid var(--border-color)",
        borderRadius: "8px"
      }}>
        
        {/* Slack-style Left Sidebar (width 260px, solid slate dark) */}
        <div style={{ 
          width: "260px", 
          display: "flex", 
          flexDirection: "column",
          backgroundColor: "#1b202c",
          borderRight: "1px solid var(--border-color)"
        }}>
          
          {/* Sidebar Header */}
          <div style={{ 
            padding: "1rem", 
            borderBottom: "1px solid rgba(255,255,255,0.06)",
            display: "flex",
            flexDirection: "column",
            gap: "0.25rem"
          }}>
            <h3 style={{ margin: 0, color: "#ffffff", fontSize: "0.95rem", fontWeight: "800", letterSpacing: "-0.2px" }}>
              Atlas Control Workspace
            </h3>
            <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
              <span style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#2ecc71", display: "inline-block" }}></span>
              <span style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.7)", fontWeight: "500" }}>
                {currentUserFullName}
              </span>
            </div>
          </div>

          {/* Search bar */}
          <div style={{ padding: "0.75rem", position: "relative" }}>
            <span style={{ 
              position: "absolute", 
              left: "1.25rem", 
              top: "50%", 
              transform: "translateY(-50%)", 
              color: "rgba(255,255,255,0.4)", 
              display: "flex", 
              alignItems: "center" 
            }}>
              <Search size={13} />
            </span>
            <input 
              type="text" 
              placeholder="Jump to..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: "100%",
                padding: "0.35rem 0.5rem 0.35rem 1.85rem",
                fontSize: "0.75rem",
                borderRadius: "4px",
                border: "none",
                backgroundColor: "rgba(255,255,255,0.06)",
                color: "#ffffff",
                outline: "none"
              }}
            />
          </div>

          {/* Directory Scroll List */}
          <div style={{ flex: 1, overflowY: "auto", padding: "0.5rem 0" }}>
            
            {/* 1. CHANNELS GROUP */}
            <div style={{ marginBottom: "1.25rem" }}>
              <div style={{ 
                display: "flex", 
                justifyContent: "space-between", 
                alignItems: "center", 
                padding: "0.25rem 1rem",
                color: "rgba(255,255,255,0.5)",
                fontSize: "0.72rem",
                textTransform: "uppercase",
                fontWeight: "700",
                letterSpacing: "0.5px"
              }}>
                <span>Channels</span>
                <button 
                  onClick={handleCreateChannel}
                  style={{ background: "none", border: "none", color: "rgba(255,255,255,0.6)", cursor: "pointer", display: "flex", padding: 0 }}
                  title="Create a Channel"
                >
                  <Plus size={14} />
                </button>
              </div>

              <div style={{ display: "flex", flexDirection: "column" }}>
                {channels.map((chan) => (
                  <div
                    key={chan.id}
                    onClick={() => setActiveChat(chan)}
                    onContextMenu={(e) => handleChannelContextMenu(e, chan)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "0.35rem 1rem",
                      cursor: "pointer",
                      backgroundColor: activeChat && activeChat.id === chan.id ? "var(--primary)" : "transparent",
                      color: activeChat && activeChat.id === chan.id 
                        ? "#ffffff" 
                        : (isChatUnreadForMe(chan) ? "red" : "rgba(255,255,255,0.75)"),
                      fontSize: "0.82rem",
                      fontWeight: activeChat && activeChat.id === chan.id ? "600" : (isChatUnreadForMe(chan) ? "700" : "400"),
                      gap: "0.45rem",
                      transition: "background 0.15s ease"
                    }}
                    className="slack-item-hover"
                  >
                    <span style={{ color: activeChat && activeChat.id === chan.id ? "#ffffff" : "rgba(255,255,255,0.4)" }}>#</span>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {chan.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* 2. DIRECT MESSAGES GROUP */}
            <div>
              <div style={{ 
                display: "flex", 
                justifyContent: "space-between", 
                alignItems: "center", 
                padding: "0.25rem 1rem",
                color: "rgba(255,255,255,0.5)",
                fontSize: "0.72rem",
                textTransform: "uppercase",
                fontWeight: "700",
                letterSpacing: "0.5px"
              }}>
                <span>Direct Messages</span>
                <button 
                  onClick={handleStartDM}
                  style={{ background: "none", border: "none", color: "rgba(255,255,255,0.6)", cursor: "pointer", display: "flex", padding: 0 }}
                  title="Start a Direct Message"
                >
                  <Plus size={14} />
                </button>
              </div>

              <div style={{ display: "flex", flexDirection: "column" }}>
                {dms.map((dm) => {
                  const resolvedName = getDMDisplayName(dm);
                  const isSelected = activeChat && activeChat.id === dm.id;
                  return (
                    <div
                      key={dm.id}
                      onClick={() => setActiveChat(dm)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "0.35rem 1rem",
                        cursor: "pointer",
                        backgroundColor: isSelected ? "var(--primary)" : "transparent",
                        color: isSelected 
                          ? "#ffffff" 
                          : (isChatUnreadForMe(dm) ? "red" : "rgba(255,255,255,0.75)"),
                        fontSize: "0.82rem",
                        fontWeight: isSelected ? "600" : (isChatUnreadForMe(dm) ? "700" : "400"),
                        gap: "0.45rem",
                        transition: "background 0.15s ease"
                      }}
                      className="slack-item-hover"
                    >
                      <span style={{ 
                        width: "8px", 
                        height: "8px", 
                        borderRadius: "50%", 
                        backgroundColor: "#2ecc71", 
                        display: "inline-block",
                        flexShrink: 0
                      }}></span>
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {resolvedName}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

          </div>

        </div>

        {/* Right Active Message Window */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", backgroundColor: "rgba(16, 18, 24, 0.4)" }}>
          {activeChat ? (
            <>
              {/* Slack Header */}
              {(() => {
                const creatorEmail = activeChat.createdByEmail;
                const currentUserDoc = users.find(u => u.email?.toLowerCase() === currentUser?.email?.toLowerCase());
                const currentUserRole = currentUserDoc?.role || "Admin";
                const isCreator = creatorEmail && creatorEmail.toLowerCase() === currentUser?.email?.toLowerCase();
                const isAdmin = currentUserRole === "Admin";
                const canManageMembers = activeChat.type === "channel" && (isCreator || isAdmin);

                return (
                  <div style={{ 
                    padding: "0.75rem 1.5rem", 
                    borderBottom: "1px solid var(--border-color)", 
                    display: "flex", 
                    justifyContent: "space-between", 
                    alignItems: "center",
                    backgroundColor: "rgba(10, 12, 18, 0.3)"
                  }}>
                    <div>
                      <h4 style={{ margin: 0, color: "var(--text-primary)", fontSize: "0.95rem", fontWeight: "700", display: "flex", alignItems: "center", gap: "0.35rem" }}>
                        {activeChat.type === "channel" ? "#" : "@"}
                        {activeChat.type === "channel" ? activeChat.name : getDMDisplayName(activeChat)}
                      </h4>
                      <p style={{ margin: "0.15rem 0 0 0", fontSize: "0.72rem", color: "var(--text-secondary)" }}>
                        {activeChat.type === "channel" 
                          ? (activeChat.poId ? `Workspace channel linked to PO` : `Discussion workspace channel`) 
                          : `Private direct message stream`}
                      </p>
                    </div>
                    {canManageMembers && (
                      <button
                        onClick={() => setIsManageMembersModalOpen(true)}
                        style={{
                          padding: "0.35rem 0.75rem",
                          fontSize: "0.72rem",
                          borderRadius: "4px",
                          backgroundColor: "rgba(255, 255, 255, 0.05)",
                          border: "1px solid var(--border-color)",
                          color: "var(--text-primary)",
                          cursor: "pointer",
                          fontWeight: "600"
                        }}
                      >
                        Manage Members
                      </button>
                    )}
                  </div>
                );
              })()}

              {/* Slack-style Message Stream (Avatar-based chronological list) */}
              <div style={{ 
                flex: 1, 
                overflowY: "auto", 
                padding: "1.5rem", 
                display: "flex", 
                flexDirection: "column", 
                gap: "1.25rem" 
              }}>
                {messages.map((msg, index) => {
                  const initialLetter = (msg.sender || "U").substring(0, 1).toUpperCase();
                  const isSenderMe = msg.senderId === currentUser?.uid || msg.senderEmail?.toLowerCase() === currentUser?.email?.toLowerCase();
                  
                  const senderDoc = users.find(u => u.email?.toLowerCase() === msg.senderEmail?.toLowerCase() || u.uid === msg.senderId);
                  const senderPhoto = senderDoc?.photoURL;

                  return (
                    <div 
                      key={msg.id || index}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "0.75rem",
                        padding: "0.15rem 0.5rem",
                        borderRadius: "6px"
                      }}
                      className="slack-msg-row"
                    >
                      {/* Left Avatar Circle */}
                      <div style={{
                        width: "36px",
                        height: "36px",
                        borderRadius: "4px",
                        backgroundColor: isSenderMe ? "var(--primary)" : (msg.senderEmail === "admin@jimini.com" ? "#e74c3c" : "#5a6275"),
                        color: "#ffffff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: "700",
                        fontSize: "0.9rem",
                        flexShrink: 0,
                        overflow: "hidden"
                      }}>
                        {senderPhoto ? (
                          <img src={senderPhoto} alt={msg.sender} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        ) : (
                          initialLetter
                        )}
                      </div>

                      {/* Right Block */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {/* Header Line (Name + Timestamp) */}
                        <div style={{ display: "flex", alignItems: "baseline", gap: "0.5rem", marginBottom: "0.2rem" }}>
                          <span style={{ fontSize: "0.85rem", fontWeight: "700", color: "var(--text-primary)" }}>
                            {msg.sender}
                          </span>
                          <span style={{ fontSize: "0.65rem", color: "var(--text-secondary)" }}>
                            {msg.time}
                          </span>
                        </div>
                        {/* Message Content */}
                        {msg.text && !msg.gifUrl && msg.type !== "gif" && (
                          <div style={{ 
                            fontSize: "0.82rem", 
                            color: "var(--text-primary)", 
                            lineHeight: "1.45",
                            wordBreak: "break-word",
                            whiteSpace: "pre-wrap"
                          }}>
                            {msg.text}
                          </div>
                        )}

                        {/* Inline GIF rendering if present */}
                        {msg.gifUrl && (
                          <div style={{ 
                            marginTop: "0.4rem", 
                            maxWidth: "320px", 
                            borderRadius: "8px", 
                            overflow: "hidden", 
                            border: "1px solid var(--border-color)", 
                            backgroundColor: "rgba(0,0,0,0.2)" 
                          }}>
                            <img 
                              src={msg.gifUrl} 
                              alt="GIF" 
                              style={{ width: "100%", maxHeight: "240px", objectFit: "contain", display: "block" }} 
                              loading="lazy" 
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                
                {messages.length === 0 && (
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "4rem 0", color: "var(--text-secondary)", gap: "0.5rem" }}>
                    <MessageSquare size={32} style={{ opacity: 0.5 }} />
                    <p style={{ margin: 0, fontSize: "0.8rem" }}>
                      This is the very beginning of your conversation stream.
                    </p>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>

              {/* Slack Floating Message Input Box */}
              <div style={{ padding: "0 1.5rem 1.5rem 1.5rem" }}>
                <form 
                  onSubmit={handleSendMessage}
                  style={{ 
                    border: "1px solid var(--border-color)", 
                    borderRadius: "8px", 
                    display: "flex", 
                    flexDirection: "column",
                    backgroundColor: "var(--bg-input)",
                    overflow: "visible",
                    position: "relative"
                  }}
                >
                  {/* Floating Emoticon & GIPHY Picker Popover */}
                  <ChatEmojiGiphyPicker
                    isOpen={isPickerOpen}
                    onClose={() => setIsPickerOpen(false)}
                    onSelectEmoji={handleSelectEmoji}
                    onSelectGif={handleSelectGif}
                  />

                  <textarea
                    rows={1}
                    placeholder={`Message ${activeChat.type === "channel" ? `#${activeChat.name}` : `@${getDMDisplayName(activeChat)}`}`}
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage(e);
                      }
                    }}
                    style={{
                      width: "100%",
                      padding: "0.75rem 1rem",
                      border: "none",
                      backgroundColor: "transparent",
                      color: "var(--text-primary)",
                      fontSize: "0.82rem",
                      resize: "none",
                      outline: "none"
                    }}
                  />
                  <div style={{ 
                    display: "flex", 
                    justifyContent: "space-between", 
                    alignItems: "center",
                    padding: "0.4rem 0.75rem",
                    borderTop: "1px solid rgba(255,255,255,0.02)",
                    backgroundColor: "rgba(0,0,0,0.1)",
                    borderBottomLeftRadius: "8px",
                    borderBottomRightRadius: "8px"
                  }}>
                    {/* Left Plugin Buttons */}
                    <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                      <button
                        type="button"
                        onClick={() => setIsPickerOpen(!isPickerOpen)}
                        style={{
                          background: isPickerOpen ? "var(--primary-light)" : "transparent",
                          border: "1px solid",
                          borderColor: isPickerOpen ? "var(--primary)" : "transparent",
                          color: isPickerOpen ? "var(--primary)" : "var(--text-secondary)",
                          cursor: "pointer",
                          padding: "0.3rem 0.55rem",
                          borderRadius: "4px",
                          display: "flex",
                          alignItems: "center",
                          gap: "0.35rem",
                          fontSize: "0.74rem",
                          fontWeight: "600",
                          transition: "all 0.15s ease"
                        }}
                        title="Add Emoticons or GIPHY GIFs"
                      >
                        <Smile size={15} />
                        <span>GIF / Emoji</span>
                      </button>
                    </div>

                    {/* Right Action Buttons */}
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <button 
                        type="submit" 
                        className="btn btn-primary"
                        disabled={!messageInput.trim()}
                        style={{ 
                          padding: "0.35rem 0.85rem", 
                          display: "flex", 
                          alignItems: "center", 
                          justifyContent: "center",
                          fontSize: "0.75rem",
                          borderRadius: "4px"
                        }}
                      >
                        <Send size={12} style={{ marginRight: "0.35rem" }} /> Send
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </>
          ) : (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "1rem" }}>
              <div style={{
                width: "60px",
                height: "60px",
                borderRadius: "50%",
                backgroundColor: "rgba(255,255,255,0.02)",
                border: "1px solid var(--border-color)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "var(--text-secondary)"
              }}>
                <MessageSquare size={28} />
              </div>
              <div style={{ textAlign: "center" }}>
                <h4 style={{ margin: 0, color: "var(--text-primary)", fontSize: "0.95rem", fontWeight: "700" }}>Welcome to Chat</h4>
                <p style={{ margin: "0.25rem 0 0 0", color: "var(--text-secondary)", fontSize: "0.78rem" }}>
                  Select a DM conversation or workspace channel from the sidebar directory to begin.
                </p>
              </div>
            </div>
          )}
        </div>

      </div>

      {/* Start DM Selection Modal */}
      <StartDMModal
        isOpen={isDMModalOpen}
        onClose={() => setIsDMModalOpen(false)}
        users={users}
        currentUserEmail={currentUser?.email}
        onSelectUser={handleStartDMFromModal}
      />

      {/* Create Channel Modal */}
      <CreateChannelModal
        isOpen={isCreateChannelModalOpen}
        onClose={() => setIsCreateChannelModalOpen(false)}
        users={users}
        currentUserEmail={currentUser?.email}
        onCreate={handleCreateChannelFromModal}
      />

      {/* Manage Members Modal */}
      <ManageChannelMembersModal
        isOpen={isManageMembersModalOpen}
        onClose={() => setIsManageMembersModalOpen(false)}
        channel={activeChat}
        users={users}
        currentUserEmail={currentUser?.email}
      />

      {/* Right Click Context Menu */}
      {contextMenu && (
        <div style={{
          position: "fixed",
          top: contextMenu.y,
          left: contextMenu.x,
          backgroundColor: "#1b202c",
          border: "1px solid var(--border-color)",
          borderRadius: "4px",
          padding: "0.25rem 0",
          zIndex: 1200,
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.4)",
          minWidth: "120px"
        }}>
          <button
            onClick={() => {
              handleDeleteChannel(contextMenu.channel);
              setContextMenu(null);
            }}
            style={{
              width: "100%",
              padding: "0.5rem 1rem",
              backgroundColor: "transparent",
              border: "none",
              color: "#e74c3c",
              fontSize: "0.78rem",
              textAlign: "left",
              cursor: "pointer",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              gap: "0.4rem"
            }}
          >
            Delete Channel
          </button>
        </div>
      )}
    </div>
  );
}
